import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { isNil, noop } from 'lodash-es'
import {
  EntityIdAndType,
  EntityType,
  EntityTypeEnum,
  Organisation,
  OrganisationMeta,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  WorkflowActions
} from 'api'
import { Locale, Translation } from 'locale'
import { createWorkflowReferenceDataAvailableEventsSelector, createWorkflowReferenceDataEntitySelector } from 'selectors/workflowSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { RadioButtonStripField, TextButton, TextButtonType } from 'view/components/buttons'
import { DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { ValidationIssuesContainer } from 'view/shared/error'
import { OrgMetaCounterpartySelectboxField, OrgMetaProductSectorSelectboxField, OrgMetaProductSelectboxField } from 'view/shared/entity'
import { OrgMetaCounterpartyOrganisationSelectboxField, OrgMetaTradingEntitySelectboxField } from 'view/shared/entity/'
import { currentOrganisationIdSelector } from 'selectors/adminSelectors'

export type TradeSplitDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  workflowId: string
  entityId: string
  organisationId?: string // Admin only
  updateOnly?: boolean
  onClose?: () => unknown
}

interface StateProps {
  organisationMetaEntity: ReferenceDataEntityType<OrganisationMeta> | null
  availableEvents: ReferenceDataManagementEvent[]
  currentOrganisationId: string | null
  getOrganisation: (organisationId: string) => Organisation | null
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: OrganisationMeta; workflowId: string } & EntityIdAndType) => unknown
  onDelete: (entitiesToDelete: EntityIdAndType[], workflowId: string) => unknown // Admin only
  onSubmitWorkflow: (workflow: string) => unknown
  onCancelWorkflow: (workflow: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: TradeSplitDialogProps) => ownProps.workflowId
const entityIdSelector = (_state: {}, ownProps: TradeSplitDialogProps) => ownProps.entityId

const mapStateToProps = createStructuredSelector({
  organisationMetaEntity: createWorkflowReferenceDataEntitySelector(EntityType.ORGANISATION_META, workflowIdSelector, entityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector),
  currentOrganisationId: currentOrganisationIdSelector,
  getOrganisation: createEntityGetterSelector(EntityType.ORGANISATION)
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onDelete: WorkflowActions.createEntityDeleteReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const Content = styled(Groupbox)`
  height: 400px;
  width: 510px;
  padding: 20px 20px 0 20px;
  overflow: auto;
`

const TradeSplitDialogComponent: React.FunctionComponent<TradeSplitDialogProps> = props => {
  const {
    workflowId,
    entityId,
    organisationMetaEntity,
    organisationId,
    currentOrganisationId,
    availableEvents,
    updateOnly,
    getOrganisation,
    onUpdate,
    onDelete,
    onSubmitWorkflow,
    onCancelWorkflow,
    onClose
  } = props

  if (!organisationMetaEntity || (isNil(currentOrganisationId) && isNil(organisationId))) {
    return null
  }

  const organisationMeta = organisationMetaEntity.data
  const organisation = getOrganisation(organisationMeta.ownerOrganisationId)
  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const validationIssues = organisationMetaEntity.validationIssues ?? []
  const hasValidationIssues = validationIssues.length > 0

  const onUpdateTradeSplit = (partialOrganisationMeta: Partial<OrganisationMeta>) => {
    onUpdate({
      entityId: organisationMeta.id,
      entityType: EntityTypeEnum.ORGANISATION_META,
      updatedEntity: { ...organisationMeta, ...partialOrganisationMeta },
      workflowId
    })
  }

  const updateHandler = <K extends keyof OrganisationMeta>(key: K) => {
    return (value: OrganisationMeta[K]) => {
      onUpdateTradeSplit({ [key]: value })
    }
  }

  const updateTradingEntityIdHandler = (value: string) => {
    onUpdateTradeSplit({
      ownerTradingEntityId: value,
      subjectOrganisationId: null,
      subjectTradingEntityId: null,
      productId: null,
      productSectorId: null
    })
  }

  const updateCounterpartyOrganisationIdHandler = (value: string) => {
    onUpdateTradeSplit({
      subjectOrganisationId: value,
      subjectTradingEntityId: null,
      productId: null,
      productSectorId: null
    })
  }

  const updateCounterpartyIdHandler = (value: string) => {
    onUpdateTradeSplit({
      subjectTradingEntityId: value,
      productId: null,
      productSectorId: null
    })
  }

  const updateProductIdHandler = (value: string) => {
    onUpdateTradeSplit({
      productId: value,
      productSectorId: null
    })
  }

  const updateSplitTradesHandler = (value: Translation) => {
    updateHandler('splitTrades')(value === Translation.UC_ON)
  }

  const updateTradeSplitAmount = (splitTradeAmount: number) => {
    onUpdateTradeSplit({ splitTradeAmount })
  }

  const onSave = () => {
    if (!updateOnly) {
      onSubmitWorkflow(workflowId)
    }
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    if (!updateOnly) {
      onCancelWorkflow(workflowId)
    } else {
      onDelete([{ entityId, entityType: EntityTypeEnum.ORGANISATION_META }], workflowId)
    }
    if (onClose) {
      onClose()
    }
  }

  useEffect(() => {
    if (organisationMeta.ownerOrganisationId === null) {
      updateHandler('ownerOrganisationId')(organisationId ?? currentOrganisationId ?? '')
    }
    if (organisationMeta.splitTrades === null) {
      updateHandler('splitTrades')(false)
    }
  }, [organisationMeta])

  return (
    <ModalWindow title={Locale.translate(Translation.TC_NEW_TRADE_SPLIT)}>
      <Content>
        <ValidationIssuesContainer>
          {validationIssues
            ?.filter(issue => issue.fieldName === 'organisationMeta')
            .map((issue, index) => (
              <li key={index}>{issue.errorMessage}</li>
            ))}
        </ValidationIssuesContainer>
        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId="TradeSplitDialog-TextInputField-MyFirm"
              label={Locale.translate(Translation.SC_ORGANISATION_META_MY_FIRM)}
              value={organisation?.legalName}
              onUpdate={noop}
              readOnly={true}
            />
          </DialogField>
          <DialogField>
            <OrgMetaTradingEntitySelectboxField
              dataTestId="TradeSplitDialog-DropDown-MyEntity"
              fieldName="ownerTradingEntityId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_MY_ENTITY)}
              organisationId={organisationMeta.ownerOrganisationId}
              selectedTradingEntityId={organisationMeta.ownerTradingEntityId}
              onUpdate={updateTradingEntityIdHandler}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <OrgMetaCounterpartyOrganisationSelectboxField
              dataTestId="TradeSplitDialog-DropDown-CounterpartyFirm"
              fieldName="subjectOrganisationId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_COUNTERPARTY_FIRM)}
              organisationId={organisationMeta.ownerOrganisationId}
              selectedTradingEntityId={organisationMeta.ownerTradingEntityId}
              selectedCounterpartyOrganisationId={organisationMeta.subjectOrganisationId}
              onUpdate={updateCounterpartyOrganisationIdHandler}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <OrgMetaCounterpartySelectboxField
              dataTestId="TradeSplitDialog-DropDown-Counterparty"
              fieldName="subjectTradingEntityId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_COUNTERPARTY)}
              selectedTradingEntityId={organisationMeta.ownerTradingEntityId}
              selectedCounterpartyOrganisationId={organisationMeta.subjectOrganisationId}
              selectedCounterpartyId={organisationMeta.subjectTradingEntityId}
              onUpdate={updateCounterpartyIdHandler}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <OrgMetaProductSelectboxField
              dataTestId="TradeSplitDialog-DropDown-Product"
              fieldName="productId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_PRODUCT)}
              organisationId={organisationMeta.ownerOrganisationId}
              selectedTradingEntityId={organisationMeta.ownerTradingEntityId}
              selectedProductId={organisationMeta.productId}
              onUpdate={updateProductIdHandler}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <OrgMetaProductSectorSelectboxField
              dataTestId="TradeSplitDialog-DropDown-ProductSector"
              fieldName="productSectorId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_PRODUCT_SECTOR)}
              organisationId={organisationMeta.ownerOrganisationId}
              selectedTradingEntityId={organisationMeta.ownerTradingEntityId}
              selectedProductId={organisationMeta.productId}
              selectedProductSectorId={organisationMeta.productSectorId}
              onUpdate={updateHandler('productSectorId')}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <NumericInputField
              dataTestId="TradeSplitDialog-RadioButtonStripField-SplitTradesAmount"
              fieldName="splitTradeAmount"
              label={Locale.translate(Translation.SC_TRADE_SPLIT_SPLIT_AMOUNT)}
              value={organisationMeta.splitTradeAmount}
              disabled={readOnly}
              validationIssues={validationIssues}
              onUpdate={updateTradeSplitAmount}
            />
          </DialogField>
          <DialogField>
            <RadioButtonStripField
              dataTestId="TradeSplitDialog-RadioButtonStripField-SplitTrades"
              label={Locale.translate(Translation.SC_TRADE_SPLIT_SPLIT_TRADES)}
              options={[Translation.UC_ON, Translation.UC_OFF]}
              selectedOption={organisationMeta.splitTrades ? Translation.UC_ON : Translation.UC_OFF}
              buttonWidth={80}
              disabled={readOnly}
              onUpdate={updateSplitTradesHandler}
            />
          </DialogField>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId="TradeSplitDialog-TextButton-Cancel" type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId="TradeSplitDialog-TextButton-Save"
              type={TextButtonType.Primary}
              disabled={readOnly || hasValidationIssues}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const TradeSplitDialog = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(TradeSplitDialogComponent)
